import React, {useCallback, useEffect, useState, useRef} from 'react';
import classNames from "classnames";
import Observer from '@researchgate/react-intersection-observer';


/**
 * Component that renders the chooser for selecting different video playlists.
 *
 * @param {Array} currentPlaylistName - The currently selected playlist.
 * @param {function} onClick - Function to be called when the chooser is clicked.
 * @param {function} isOpen - Controls whether or not the chooser is currently open.
 */
export const VideoPlaylistChooser = React.memo(({currentPlaylistName, onClick, isOpen}) => {
    const selectorIconClasses = classNames({
        'filter': !isOpen,
        'close': isOpen
    });
    const chooserClasses = classNames('video-playlist-chooser', {'video-playlist-chooser--open': isOpen});

    return (
        <div className={chooserClasses}>
            <div className="video-playlist-chooser__header" data-testid="chooser-header" onClick={onClick}>
                <div className="video-playlist-chooser__header-left">
                    <span className="video-playlist-chooser__label">
                        Channel
                    </span>
                    <div className="video-playlist-chooser__title">
                        {isOpen === false && currentPlaylistName}
                        {isOpen === true && "Select a channel"}
                    </div>
                </div>
                <div className="video-playlist-chooser__header-right" data-testid="chooser-header-right">
                    {isOpen === false && "Filter"}
                    <i className={selectorIconClasses}>
                    </i>
                </div>
            </div>
        </div>
    );
});

/**
 * Video playlist component that will configure the given videojs instance with the currently selected playlist.
 *
 * @param playlistNames - The name of the playlists that the user can filter by.
 * @param isLoading - Whether or not a loading indicator should be displayed.
 * @param onPlaylistChange - Function to be called when a new playlist has been selected. Gets passed the
 *  index of the selected playlist.
 * @param onEndReached - Function to be called when the bottom of the playlist has been reached.
 */
const VideoPlaylist = React.memo(function VideoPlaylist({playlistNames, isLoading, onPlaylistChange, onEndReached, enableInfiniteScrolling}) {
    const playlistContainer = useRef(null);
    const [chooserOpen, setChooserOpen] = useState(false);
    const [selectedPlaylist, setSelectedPlaylist] = useState('');
    const [selectedPlaylistIndex, setSelectedPlaylistIndex] = useState(0);

    // When new playlist is selected update the selected playlist and notify the parent components.
    useEffect(() => {
        setSelectedPlaylist(playlistNames[selectedPlaylistIndex]);
        onPlaylistChange(selectedPlaylistIndex);
    }, [onPlaylistChange, playlistNames, selectedPlaylistIndex]);

    const playlistClasses = classNames('video-playlist', {'video-playlist--chooser-open': chooserOpen});

    const observerChangeHandler = useCallback((entry) => {
        if (entry.isIntersecting && !isLoading) {
            onEndReached();
        }
    }, [onEndReached, isLoading]);

    const toggleChooserOpen = useCallback(() => {
        setChooserOpen((prevState => !prevState));
    }, []);

    // Handle the playlist being changed.
    const playlistSelected = useCallback((event) => {
        event.stopPropagation();
        const newPlaylistIndex = parseInt(event.target.value, 10);
        setSelectedPlaylistIndex(newPlaylistIndex);
        toggleChooserOpen();
    }, [toggleChooserOpen]);

    return (
        <div ref={playlistContainer} className={playlistClasses}>
            <VideoPlaylistChooser currentPlaylistName={selectedPlaylist} onClick={toggleChooserOpen}
                                  isOpen={chooserOpen}/>
            <div className="video-playlist__container">
                <div className="video-playlist__videos">
                    <div className="vjs-playlist"></div>
                    {isLoading === true && (
                        <div className="video-playlist__loader">
                            <div className="anvil-loader"></div>
                            Loading videos...
                        </div>
                    )}
                    <Observer root='.video-playlist__videos' onChange={observerChangeHandler}
                              disabled={!enableInfiniteScrolling} rootMargin="0% 0% 400px">
                        <span className="video-playlist__load-trigger"></span>
                    </Observer>
                </div>
                <div className="video-playlist__playlists">
                    {playlistNames.map((playlist, index) => (
                        <div key={index} className="video-playlist__items">
                            <label htmlFor={`playlist${index}`} className="video-playlist__item-name">
                                {playlist}
                            </label>
                            <input type="radio" id={`playlist${index}`} name="playlist" defaultValue={index}
                                   defaultChecked={index === selectedPlaylistIndex} onChange={playlistSelected}/>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
});

export default VideoPlaylist;
